import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiGetShowRoadmap, apiCreateShowRoadmapItem, apiUpdateShowRoadmapItem, apiDeleteShowRoadmapItem,
  apiGetShowDistances, apiCreateShowDistance, apiUpdateShowDistance, apiDeleteShowDistance,
  apiCalculateDistances,
  apiUpdateShow,
  apiAiAssist, apiAiCredits,
  type RoadmapItemDTO,
  type DistanceDTO,
  type VenueDTO,
} from '../api';
import ContactPersonsSection from './ContactPersonsSection';
import CurrencyConverter from './CurrencyConverter';
import AutoTextarea from './AutoTextarea';
import { resolveShowCurrency, resolveBandCurrency, countryToCurrency, isoFromCurrencyNote, CURRENCY_CATALOG } from '../lib/countryCurrency';
import { countryToTimezone, timezoneOffsetLabel, formatTimezoneUtc } from '../lib/countryTimezone';
import { reportSaveError, reportSaveOk } from '../lib/saveStatus';
import { createWriteQueue } from '../lib/writeQueue';
import { isDaySheetFieldShown } from '../lib/showTypes';
import { useWriteGate } from '../lib/writeGate';
import { withCardIcon } from '../lib/uiIcons';

/**
 * Closed list of roadmap categories (`category` column). Served by the native
 * `<select>` of each schedule row — a `<datalist>` does not open reliably on
 * iPad/Safari. `doors` / `curfew` come from the real DB values. A legacy value
 * outside this list is never rewritten: it shows through the "Other…"
 * free-text option (single exported source for the selector).
 */
export const SCHEDULE_CATEGORIES: string[] = [
  '', 'travel', 'show', 'load-in', 'soundcheck', 'doors', 'curfew',
  'shuttle', 'hotel', 'meeting', 'interview', 'after-show', 'off', 'lunch', 'dinner',
];
/** Sentinel option value that reveals the free-text category field ("Other…"). */
export const OTHER_CATEGORY = '__other__';

// Day-info fields stored on the show itself (columns added to tour_dates).
const DAY_FIELDS: { key: string; labelKey: string; type?: string; phKey?: string; full?: boolean; currency?: boolean; right?: boolean }[] = [
  { key: 'welcome_note', labelKey: 'itinerary.welcomeNote', full: true, phKey: 'itinerary.welcomeNotePh' },
  { key: 'electricity', labelKey: 'itinerary.electricity', phKey: 'itinerary.electricityPh' },
  { key: 'wifi_venue', labelKey: 'itinerary.venueWifi', phKey: 'itinerary.venueWifiPh' },
  // Timezone = une LIGNE À PART ENTIÈRE (2026-11-02, lot U2) : nichée dans la
  // cellule de « Venue Wi-Fi », elle démarrait à x1062 sous un libellé à x988 —
  // deux champs dans une seule cellule = deux axes. Elle suit maintenant la
  // grille commune de la day sheet.
  { key: 'timezone', labelKey: 'itinerary.timezone', phKey: 'itinerary.timezonePh' },
  { key: 'currency_note', labelKey: 'itinerary.currency', currency: true },
  { key: 'weather', labelKey: 'itinerary.weather', phKey: 'itinerary.weatherPh', full: true },
  { key: 'luggage_notes', labelKey: 'itinerary.luggage', full: true, phKey: 'itinerary.luggagePh' },
  { key: 'stage_name', labelKey: 'itinerary.stageName', phKey: 'itinerary.stageNamePh' },
  { key: 'door_time', labelKey: 'itinerary.doorsOpen', type: 'time', phKey: 'itinerary.doorsOpenPh' },
  { key: 'running_order', labelKey: 'itinerary.runningOrder', full: true, phKey: 'itinerary.runningOrderPh' },
  { key: 'duration_minutes', labelKey: 'itinerary.showDuration', type: 'number', phKey: 'itinerary.showDurationPh', full: true },
  { key: 'passport_reminder', labelKey: 'itinerary.passportReminder', phKey: 'itinerary.passportReminderPh' },
  { key: 'artist_pass', labelKey: 'itinerary.artistPass', phKey: 'itinerary.artistPassPh' },
];

// Map an Electricity description (e.g. "230V 50Hz Euro Schuko", "Type G (UK)",
// "Swiss J", "NEMA") to the local plug photos that apply, so the tour can see
// which adapter(s) are needed. (2026-08-12)
const PLUG_IMAGES: { key: string; src: string; label: string }[] = [
  { key: 'c', src: '/plug-types/type-c-sm.jpg', label: 'Type C (Europlug)' },
  { key: 'ef', src: '/plug-types/type-ef-sm.jpg', label: 'Type E/F (Schuko)' },
  { key: 'g', src: '/plug-types/type-g-sm.jpg', label: 'Type G (UK)' },
  { key: 'j', src: '/plug-types/type-j-sm.jpg', label: 'Type J (Swiss)' },
  { key: 'i', src: '/plug-types/type-i-sm.jpg', label: 'Type I (AUS/NZ)' },
  { key: 'ab', src: '/plug-types/type-ab-sm.jpg', label: 'Type A/B (NEMA)' },
  { key: 'n', src: '/plug-types/type-n-sm.jpg', label: 'Type N (Brazil)' },
];
function plugImagesFor(electricity: string | undefined): { key: string; src: string; label: string }[] {
  const s = (electricity || '').toLowerCase();
  if (!s.trim()) return [];
  const has = (re: RegExp) => re.test(s);
  const out: { key: string; src: string; label: string }[] = [];
  const pick = (key: string) => PLUG_IMAGES.find((p) => p.key === key)!;
  // Europe / Schuko → both C (Europlug) and E/F (Schuko socket)
  if (has(/schuko|euro|230v|220v|type\s*[ef]/)) { out.push(pick('ef')); if (has(/euro|schuko/)) out.push(pick('c')); }
  else if (has(/type\s*c/)) out.push(pick('c'));
  if (has(/type\s*g|\(uk\)/)) out.push(pick('g'));
  if (has(/swiss|type\s*j/)) out.push(pick('j'));
  if (has(/type\s*i|aus|nz|australia/)) out.push(pick('i'));
  if (has(/nema|type\s*[ab]/)) out.push(pick('ab'));
  if (has(/brazil|type\s*n/)) out.push(pick('n'));
  return out;
}

// The currency list is the app-wide catalog (`CURRENCY_CATALOG`, LOT 1
// 2026-11-04): the day sheet used to carry its own 29-entry copy while the
// converter, the finance selects and the label lookup each carried another —
// so a band touring Finland or Colombia could not select its currency here.

/** Compact label stored in currency_note, e.g. "CZK — Czech Koruna". */
function currencyLabel(code: string | undefined): string {
  if (!code) return '';
  const opt = CURRENCY_CATALOG.find((c) => c.code === code.toUpperCase());
  return opt ? `${opt.code} — ${opt.name}` : code;
}

/**
 * A foldable section that opens itself the first time its data is non-empty —
 * then obeys the user (LOT 1 2026-11-04: "Schedule (roadmap)", "Distances" and
 * "Contact persons" stayed shut even with 7 rows of data). Uncontrolled on
 * purpose: the DOM keeps the user's open/closed state, so nothing can fight a
 * manual close. `resetKey` = the show id: changing day restores the default.
 */
function useAutoOpenSection(hasData: boolean, resetKey?: string | null) {
  const ref = useRef<HTMLDetailsElement | null>(null);
  const touched = useRef(false);
  // New day → back to the default (closed), then the effect below re-opens it
  // when that day's data has already resolved.
  useEffect(() => {
    touched.current = false;
    if (ref.current) ref.current.open = false;
  }, [resetKey]);
  useEffect(() => {
    if (touched.current) return;
    const el = ref.current;
    if (el && hasData) el.open = true;
  }, [hasData, resetKey]);
  // The user clicked the summary: from now on we never re-open it ourselves.
  const onToggle = useCallback(() => { touched.current = true; }, []);
  return { ref, onToggle };
}

interface Props {
  show: any;
  realId: string | null;
  onDayChange?: (patch: Record<string, any>) => void;
  /** Optional export button rendered in the section header (ShowDetailPage). */
  onExportDaysheet?: () => void;
  exporting?: boolean;
  /** The show's venue — its country/currency drive the converter SOURCE. */
  venue?: Partial<VenueDTO> | null;
  /** The band's home country — drives the timezone offset & converter TARGET. */
  bandCountry?: string | null;
  /** Country of the show's accommodation (travel days): converter SOURCE. */
  accommodationCountry?: string | null;
  /** Ville de base du groupe (Band Settings) — LOT 4 (2026-11-04) : distingue un
   *  travel RETOUR (J4) d'un travel aller (J1) pour le filtrage des champs de la
   *  day sheet (électricité / passeport / devise masqués au retour). */
  bandHomeCity?: string | null;
}

export default function ItinerarySection({ show, realId, onDayChange, onExportDaysheet, exporting, venue, bandCountry, accommodationCountry, bandHomeCity }: Props) {
  const { t } = useTranslation();
  const [roadmap, setRoadmap] = useState<RoadmapItemDTO[]>([]);
  const [saving, setSaving] = useState(false);
  // Which roadmap rows have their secondary fields (responsible / phone /
  // location) UNFOLDED by the user. Default is collapsed; a row with any of
  // those fields filled is always shown expanded so existing data is never hidden.
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  // Rows where the user chose the "Other…" free-text category (vs a legacy
  // value that is simply not in the list — both render the text field).
  const [otherRows, setOtherRows] = useState<Set<string>>(new Set());

  // ---- AI copilot: check itinerary (read-only risk review) ----
  const bandId = (show as any)?.band_id || '';
  // Capability-aware write gate (lot A1): a global read-only account (even with a
  // band editor role) must not emit itinerary/roadmap/distance writes — the
  // server 403s them. Declared BEFORE every handler so the closures see it.
  const gate = useWriteGate(bandId);
  const canWrite = gate.canWrite;
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [checkResult, setCheckResult] = useState<{ summary?: string; risks?: { date?: string; issue?: string; severity?: string; suggestion?: string }[] } | null>(null);
  const [credits, setCredits] = useState<number | null>(null);
  const [unlimited, setUnlimited] = useState(false);
  useEffect(() => {
    apiAiCredits().then((d) => { setCredits(d.credits); setUnlimited(!!d.unlimited); }).catch(() => setCredits(null));
  }, []);
  const outOfCredits = !unlimited && credits !== null && credits <= 0;

  const checkItinerary = useCallback(async () => {
    if (!bandId || checking) return;
    setChecking(true);
    setCheckError(null);
    setCheckResult(null);
    try {
      const result = await apiAiAssist({ task: 'check_itinerary', bandId });
      setCheckResult((result.proposedFields ?? {}) as any);
    } catch (e: any) {
      setCheckError(e?.message || t('checkItinerary.failed'));
    } finally {
      setChecking(false);
    }
  }, [bandId, checking]);

  // Severity scale = the app's own status tokens (P4a), never a hex literal:
  // the badge only colours its TEXT, the chip keeps its token surface.
  const severityColor = (sev?: string) => {
    const s = (sev || '').toLowerCase();
    if (s === 'high') return 'var(--danger)';
    if (s === 'medium') return 'var(--warning)';
    return 'var(--success)';
  };
  const severityLabel = (sev?: string) => {
    const s = (sev || '').toLowerCase();
    if (s === 'high') return t('checkItinerary.severityHigh');
    if (s === 'medium') return t('checkItinerary.severityMedium');
    return t('checkItinerary.severityLow');
  };

  // ---- Currency (day sheet) & timezone ----
  // (LOT 1, 2026-11-04 — the travel-day / per-show override fix.)
  const isTravelDay = ((show as any)?.event_type || '').toLowerCase() === 'travel';
  const showDate = (show as any)?.date as string | undefined;
  const currencyNote = (show as any)?.currency_note as string | undefined;

  // The currency driving the `Currency` select AND the converter's SOURCE.
  // Priority:
  //   1. `currency_note` when it carries a catalog ISO code — the USER's
  //      per-show choice (the select itself writes it). It used to come LAST,
  //      so on any show whose venue had a country the select was pinned to the
  //      venue's currency and refused to change when the user picked another.
  //   2. the venue (its own `currency`, then its country),
  //   3. the accommodation's country — a travel day has no venue at all, and
  //      its money is the hotel's (was completely ignored before),
  //   4. EUR.
  const explicitCurrency = isoFromCurrencyNote(currencyNote);
  const venueCurrency = explicitCurrency
    || resolveShowCurrency(venue?.currency, venue?.country, null)
    || (accommodationCountry ? countryToCurrency(accommodationCountry) : null)
    || 'EUR';

  // Timezone: the show's timezone field holds the VENUE zone. Show the hour
  // offset relative to the band's home zone (e.g. "CET · +1h vs home").
  const bandTz = countryToTimezone(bandCountry);
  const venueTz = (show as any)?.timezone || countryToTimezone(venue?.country);
  const tzOffsetLabel = timezoneOffsetLabel(bandTz, venueTz, showDate);
  // The same zone, readable: "UTC+2" instead of "Europe/Helsinki". Shown only
  // for a real IANA id — free text typed by the user is returned verbatim by
  // formatTimezoneUtc and must not be echoed twice in the hint.
  const tzUtcPreview = venueTz ? formatTimezoneUtc(venueTz, showDate) : '';
  const tzHint = [tzUtcPreview && tzUtcPreview !== venueTz ? tzUtcPreview : '', tzOffsetLabel]
    .filter(Boolean)
    .join(' · ');

  // Currency converter — SOURCE = the currency of where the band is that day
  // (the venue's local currency for a show, or the accommodation's local
  // currency for a travel day); TARGET = the band's home currency (from the
  // band's country). So you convert what you pay at the venue/hotel into the
  // band's home money. An explicit per-show choice always wins over both.
  const bandCurrency = resolveBandCurrency(bandCountry);
  const converterFrom = isTravelDay && !explicitCurrency
    ? (countryToCurrency(accommodationCountry) || venueCurrency)
    : venueCurrency;
  const converterTo = bandCurrency;

  // Keep the latest onDayChange in a ref so updateDayField (stable useCallback)
  // can call it without re-creating itself on every render.
  const onDayChangeRef = useRef(onDayChange);
  useEffect(() => { onDayChangeRef.current = onDayChange; }, [onDayChange]);

  const load = useCallback(async () => {
    if (!realId) return;
    const r = await apiGetShowRoadmap(realId).catch(() => []);
    setRoadmap([...r].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
  }, [realId]);

  useEffect(() => { void load(); }, [load]);

  // ---- Day info (fields on the show) ----
  // Optimistic + debounced (LOT TYPING-AND-SETLIST-LAYOUT, 2026-11-04): the
  // field was written to `apiUpdateShow` on EVERY keystroke (one PUT + one
  // `setSaving(true)` per character). Now the local show state is updated
  // synchronously and the accumulated patch is persisted once, 500 ms after the
  // last keystroke.
  const dayQueue = useRef(createWriteQueue()).current;
  const pendingDayPatch = useRef<Record<string, any>>({});
  useEffect(() => () => { dayQueue.flushAll(); }, [dayQueue]);

  // Stable callback (useCallback): the converter's effect depends on the prop
  // identity — a new function every render would re-trigger writes (loop).
  const updateDayField = useCallback((key: string, value: any) => {
    if (!canWrite) return;
    // Local state first — the input is the truth, never the network reply.
    onDayChangeRef.current?.({ [key]: value });
    if (!realId) return;
    pendingDayPatch.current = { ...pendingDayPatch.current, [key]: value };
    dayQueue.schedule('show', async () => {
      const patch = pendingDayPatch.current;
      pendingDayPatch.current = {};
      setSaving(true);
      try {
        await apiUpdateShow(realId, patch);
        reportSaveOk('show');
      } catch (e: any) {
        console.error('Failed to save day info', e);
        reportSaveError('show', e);
      } finally { setSaving(false); }
    });
  }, [realId, canWrite, dayQueue]);

  const handleRateChange = useCallback((r: string) => {
    void updateDayField('currency_rate', r);
  }, [updateDayField]);

  // ---- Roadmap ----
  const addItem = async () => {
    if (!realId || !canWrite) return;
    try {
      const item = await apiCreateShowRoadmapItem(realId, {
        time_from: '', time_to: '', category: '', activity: '', responsible: '', contact_phone: '', location: '', sort_order: roadmap.length,
      });
      setRoadmap((p) => [...p, item]);
    } catch (e) { reportSaveError('show', e); }
  };
  // Optimistic + debounced per row (LOT TYPING-AND-SETLIST-LAYOUT): typing a
  // step/activity used to PUT once per character AND re-write the row from the
  // reply ~80 ms later — which erased whatever was typed meanwhile.
  const roadmapQueue = useRef(createWriteQueue()).current;
  const roadmapPending = useRef<Map<string, RoadmapItemDTO>>(new Map());
  useEffect(() => () => { roadmapQueue.flushAll(); }, [roadmapQueue]);

  const updateItem = (it: RoadmapItemDTO, patch: Partial<RoadmapItemDTO>) => {
    if (!canWrite) return;
    // Optimistic local update so the controlled inputs (esp. <input type="time">)
    // keep the edited value immediately; persist in the background.
    const next = { ...it, ...patch };
    setRoadmap((p) => p.map((x) => (x.id === it.id ? next : x)));
    if (!realId) return;
    const key = String(it.id);
    roadmapPending.current.set(key, next);
    roadmapQueue.schedule(key, async () => {
      const payload = roadmapPending.current.get(key);
      if (!payload) return;
      roadmapPending.current.delete(key);
      try {
        await apiUpdateShowRoadmapItem(realId, it.id, payload);
      } catch (e: any) {
        // Keep the local value (it is what the user typed) and surface the
        // failure — reverting to `it` would also revert LATER edits.
        console.error('[Itinerary] roadmap save failed:', e);
        reportSaveError('show', e);
      }
    });
  };
  const removeItem = async (it: RoadmapItemDTO) => {
    if (!realId || !canWrite) return;
    roadmapQueue.cancel(String(it.id));
    roadmapPending.current.delete(String(it.id));
    try {
      await apiDeleteShowRoadmapItem(realId, it.id);
      setRoadmap((p) => p.filter((x) => x.id !== it.id));
    } catch (e) { reportSaveError('show', e); }
  };
  const moveItem = async (it: RoadmapItemDTO, dir: number) => {
    if (!canWrite) return;
    const idx = roadmap.findIndex((x) => x.id === it.id);
    const target = idx + dir;
    if (target < 0 || target >= roadmap.length) return;
    const arr = [...roadmap];
    const [cur] = arr.splice(idx, 1);
    arr.splice(target, 0, cur);
    setRoadmap(arr.map((x, i) => ({ ...x, sort_order: i })));
    await Promise.all(arr.map((x, i) => realId && apiUpdateShowRoadmapItem(realId, x.id, { ...x, sort_order: i })));
  };

  // ---- Distances (airport→hotel, hotel→venue…) ----
  const [distances, setDistances] = useState<DistanceDTO[]>([]);
  useEffect(() => {
    if (!realId) return;
    let cancelled = false;
    apiGetShowDistances(realId)
      .then((d) => { if (!cancelled) setDistances([...d].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [realId]);

  // ---- Foldable sections (LOT 1, 2026-11-04) ----
  // "Schedule (roadmap)", "Distances" and "Contact persons" opened CLOSED even
  // on a show carrying 7 schedule rows: the data was there, invisible. They now
  // unfold themselves as soon as they have something to show — and stay shut
  // for good once the user closed them.
  const [contactsHasData, setContactsHasData] = useState(false);
  const scheduleFold = useAutoOpenSection(roadmap.length > 0, realId);
  const distancesFold = useAutoOpenSection(distances.length > 0, realId);
  const contactsFold = useAutoOpenSection(contactsHasData, realId);

  const addDistance = async () => {
    if (!realId || !canWrite) return;
    const d = await apiCreateShowDistance(realId, {
      from_label: '', to_label: '', mode: 'car', distance: '', duration: '', notes: '', sort_order: distances.length,
    });
    setDistances((p) => [...p, d]);
  };
  const distanceQueue = useRef(createWriteQueue()).current;
  const distancePending = useRef<Map<string, DistanceDTO>>(new Map());
  useEffect(() => () => { distanceQueue.flushAll(); }, [distanceQueue]);

  const updateDistance = (d: DistanceDTO, patch: Partial<DistanceDTO>) => {
    if (!realId || !canWrite) return;
    // Optimistic + debounced per row — same reason as the schedule rows.
    const next = { ...d, ...patch };
    setDistances((p) => p.map((x) => (x.id === d.id ? next : x)));
    const key = String(d.id);
    distancePending.current.set(key, next);
    distanceQueue.schedule(key, async () => {
      const payload = distancePending.current.get(key);
      if (!payload) return;
      distancePending.current.delete(key);
      try { await apiUpdateShowDistance(realId, d.id, payload); }
      catch (e: any) { reportSaveError('show', e); }
    });
  };
  const removeDistance = async (d: DistanceDTO) => {
    if (!realId || !canWrite) return;
    distanceQueue.cancel(String(d.id));
    distancePending.current.delete(String(d.id));
    await apiDeleteShowDistance(realId, d.id);
    setDistances((p) => p.filter((x) => x.id !== d.id));
  };

  // Auto-compute distances from the venue + hotels (+ airport when flying):
  // calls the backend (geocodes hotel addresses when missing) and writes the
  // resulting venue↔hotel / airport↔hotel legs as distance rows.
  const [calcBusy, setCalcBusy] = useState(false);
  const [calcMsg, setCalcMsg] = useState<string | null>(null);
  const calculateDistances = async () => {
    if (!realId || !canWrite) return;
    setCalcBusy(true);
    setCalcMsg(null);
    try {
      const transport = (show as any).transport || 'van';
      const res = await apiCalculateDistances(realId, {
        transport,
        venue: { lat: venue?.lat, lng: venue?.lng, city: venue?.city, name: venue?.name },
      });
      // Merge into the distances list: clear existing auto rows? Keep manual —
      // instead append the computed legs, replacing rows with the same from/to.
      const newRows: DistanceDTO[] = [];
      const fmtMin = (min?: number | null) => (min != null ? `${min} min` : '');
      for (const r of res.results) {
        if (r.venue_km != null) newRows.push({ id: 0, tour_date_id: 0, from_label: 'Venue', to_label: r.hotel_name || 'Hotel', mode: 'car', distance: `${r.venue_km} km`, duration: fmtMin(r.venue_min), notes: 'auto' } as any);
        if (r.airport_km != null) newRows.push({ id: 0, tour_date_id: 0, from_label: 'Airport', to_label: r.hotel_name || 'Hotel', mode: 'flight', distance: `${r.airport_km} km`, duration: fmtMin(r.airport_min), notes: 'auto' } as any);
        if (r.station_km != null) newRows.push({ id: 0, tour_date_id: 0, from_label: 'Station', to_label: r.hotel_name || 'Hotel', mode: 'train', distance: `${r.station_km} km`, duration: fmtMin(r.station_min), notes: 'auto' } as any);
      }
      if (res.venue_airport_km != null) {
        newRows.push({ id: 0, tour_date_id: 0, from_label: 'Airport', to_label: 'Venue', mode: 'flight', distance: `${res.venue_airport_km} km`, duration: fmtMin(res.venue_airport_min), notes: 'auto' } as any);
      }
      if (res.venue_station_km != null) {
        newRows.push({ id: 0, tour_date_id: 0, from_label: 'Station', to_label: 'Venue', mode: 'train', distance: `${res.venue_station_km} km`, duration: fmtMin(res.venue_station_min), notes: 'auto' } as any);
      }
      // Band -> venue leg (the van leaving the band's Home base). Present only
      // when the band has stored coordinates; absent otherwise (nothing invented).
      if (res.band_leg) {
        newRows.push({ id: 0, tour_date_id: 0, from_label: res.band_leg.from_label, to_label: res.band_leg.to_label, mode: res.band_leg.mode, distance: `${res.band_leg.km} km`, duration: fmtMin(res.band_leg.min), notes: 'auto' } as any);
      }
      // HOME legs (2026-10-20, per transport mode — the UI only shows the legs
      // relevant to the mode): plane -> home→departure airport (once),
      // train -> home→departure station (once), van/tourbus -> home→hotel per
      // accommodation, van fallback -> home→venue when there is no hotel.
      const homeLabel = t('itinerary.homeBase');
      const homeAirportRow = res.results.find((r) => r.home_airport_km != null);
      if (homeAirportRow) {
        const to = res.departure_airport ? `${res.departure_airport.iata} — ${res.departure_airport.city || res.departure_airport.name}` : 'Airport';
        newRows.push({ id: 0, tour_date_id: 0, from_label: homeLabel, to_label: to, mode: 'flight', distance: `${homeAirportRow.home_airport_km} km`, duration: fmtMin(homeAirportRow.home_airport_min), notes: 'auto' } as any);
      }
      const homeStationRow = res.results.find((r) => r.home_station_km != null);
      if (homeStationRow) {
        const to = res.departure_station ? `${res.departure_station.uic} — ${res.departure_station.name}` : 'Station';
        newRows.push({ id: 0, tour_date_id: 0, from_label: homeLabel, to_label: to, mode: 'train', distance: `${homeStationRow.home_station_km} km`, duration: fmtMin(homeStationRow.home_station_min), notes: 'auto' } as any);
      }
      for (const r of res.results) {
        if (r.home_hotel_km != null) {
          newRows.push({ id: 0, tour_date_id: 0, from_label: homeLabel, to_label: r.hotel_name || 'Hotel', mode: 'car', distance: `${r.home_hotel_km} km`, duration: fmtMin(r.home_hotel_min), notes: 'auto' } as any);
        }
      }
      if (res.home_venue_km != null) {
        newRows.push({ id: 0, tour_date_id: 0, from_label: homeLabel, to_label: 'Venue', mode: 'car', distance: `${res.home_venue_km} km`, duration: fmtMin(res.home_venue_min), notes: 'auto' } as any);
      }
      if (res.home_leg_reason === 'band address not set') {
        setCalcMsg(t('itinerary.homeLegNeedsGeocode'));
      }
      // Persist each computed row via the API, then refresh the list.
      for (const row of newRows) {
        await apiCreateShowDistance(realId, {
          from_label: row.from_label, to_label: row.to_label, mode: row.mode,
          distance: row.distance, duration: row.duration, notes: row.notes, sort_order: distances.length + newRows.indexOf(row),
        });
      }
      const fresh = await apiGetShowDistances(realId);
      setDistances([...fresh].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
      setCalcMsg(`${t('itinerary.computed', { count: newRows.length })}${res.geocoded.length ? t('itinerary.computedGeocoded', { count: res.geocoded.length }) : ''}.`);
    } catch (e: any) {
      setCalcMsg(t('itinerary.computeFailed', { msg: e?.message || e }));
    } finally {
      setCalcBusy(false);
    }
  };

  return (
    <div className="card" data-daytab="itinerary" data-density="socle" style={{ marginBottom: 20 }}>
      <div className="rc-card-head">
        <h3 className="rc-header-title">{withCardIcon('show-day:daysheet', t('itinerary.daySheet'))}</h3>
        <div className="rc-header-actions">
          {bandId && (
            <button className="btn btn-secondary" onClick={() => void checkItinerary()} disabled={checking || outOfCredits}>
              {checking ? t('checkItinerary.running') : outOfCredits ? t('checkItinerary.outOfCredits') : t('checkItinerary.button')}
            </button>
          )}
          {onExportDaysheet && (
            <button className="btn btn-secondary" onClick={onExportDaysheet} disabled={exporting}>
              {exporting ? t('itinerary.generating') : t('itinerary.generateDaysheet')}
            </button>
          )}
        </div>
      </div>

      {/* AI copilot — check itinerary (read-only risk review) */}
      {bandId && (checking || checkError || checkResult) && (
        <div className="ai-import-review" style={{ marginTop: 8, marginBottom: 12 }}>
          {checking && <p style={{ color: 'var(--text-dim)' }}>{t('checkItinerary.running')}</p>}
          {!checking && checkError && (
            <div className="ai-import-error" role="alert">{checkError}</div>
          )}
          {!checking && checkResult && (
            <>
              <p className="ai-import-banner" style={{ fontSize: 'var(--font-sm)' }}>
                {t('checkItinerary.readOnly')}
              </p>
              {checkResult.summary ? (
                <p style={{ margin: '4px 0 8px', fontSize: 'var(--font-sm)' }}>{checkResult.summary}</p>
              ) : null}
              {!checkResult.risks || checkResult.risks.length === 0 ? (
                <p style={{ color: 'var(--success)', fontSize: 'var(--font-sm)', margin: 0 }}>{t('checkItinerary.noRisks')}</p>
              ) : (
                <div className="rc-list">
                  {checkResult.risks.map((r, i) => (
                    <div key={i} className="rc-card col">
                      <div className="rc-card-fields">
                        <span className="ai-transport-badge" style={{ color: severityColor(r.severity) }}>
                          {severityLabel(r.severity)}
                        </span>
                        {r.date && <strong style={{ fontSize: 'var(--font-sm)' }}>{r.date}</strong>}
                      </div>
                      {r.issue && <p style={{ margin: '6px 0 0', fontSize: 'var(--font-sm)' }}>{r.issue}</p>}
                      {r.suggestion && (
                        <p style={{ margin: '4px 0 0', fontSize: 'var(--font-sm)', color: 'var(--text-dim)' }}>
                          <strong>{t('checkItinerary.suggestion')}:</strong> {r.suggestion}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* General information — 2-column label:value grid like the SAMAEL itinerary */}
      <h4 className="rc-section-title" style={{ margin: '20px 0 10px' }}>
        {t('itinerary.generalInfo')}
      </h4>
      <div className="rc-field-grid" data-dayinfo-grid>
        {/* PROFIL PAR TYPE + RETOUR HOME (LOT 4, 2026-11-04, lib/showTypes.ts) :
            doors / running order / stage name / durée / wifi / artist pass /
            invités sont masqués sur un travel day, un day off ou en studio ;
            électricité + passeport + devise sont en plus masqués sur un travel
            RETOUR (J4, on rentre chez soi). RÈGLE D'OR : un champ qui porte déjà
            une valeur reste TOUJOURS affiché — aucune donnée n'est cachée en silence. */}
        {DAY_FIELDS.filter((f) => isDaySheetFieldShown(show, f.key, bandHomeCity)).map((f) => (
          <div className="form-row" key={f.key} style={{ gridColumn: f.full || f.currency || f.key === 'timezone' ? '1 / -1' : f.right ? 2 : undefined }}>
            <label style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              {t(f.labelKey)}
            </label>
            {f.currency ? (
              <>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <select
                    className="form-input"
                    data-currency-select
                    style={{ flex: '0 0 200px' }}
                    value={venueCurrency}
                    disabled={!canWrite}
                    onChange={(e) => updateDayField('currency_note', currencyLabel(e.target.value))}
                  >
                    {CURRENCY_CATALOG.map((c) => (
                      <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                    ))}
                    {/* A venue currency outside the catalog must still show —
                        a blank select silently reads as "EUR" to the user. */}
                    {!CURRENCY_CATALOG.some((c) => c.code === venueCurrency) && (
                      <option value={venueCurrency}>{venueCurrency}</option>
                    )}
                  </select>
                  <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                    <CurrencyConverter
                      defaultFrom={converterFrom}
                      defaultTo={converterTo}
                      onRateChange={handleRateChange}
                    />
                  </div>
                </div>
              </>
            ) : f.type === 'number' ? (
              <input className="form-input" type="number" value={(show as any)?.[f.key] ?? ''}
                disabled={!canWrite}
                onChange={(e) => updateDayField(f.key, Number(e.target.value) || 0)} />
            ) : f.key === 'electricity' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {/* Valeur COURTE (« 230V 50Hz Euro Schuko ») : une textarea
                      rows=1 rendait un champ plus haut que ses voisins et
                      cassait l'axe de la grille (2026-11-02, lot U2). */}
                  <input className="form-input" type="text" value={(show as any)?.electricity ?? ''}
                    placeholder={t(f.phKey!)}
                    disabled={!canWrite}
                    onChange={(e) => updateDayField('electricity', e.target.value)} />
                  <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    {(() => {
                      const plugs = plugImagesFor((show as any)?.electricity);
                      if (!plugs.length) return null;
                      return (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          {plugs.map((p) => (
                            <div key={p.key} style={{ textAlign: 'center', width: 74 }}>
                              <img src={p.src} alt={p.label} title={p.label} style={{ width: '100%', borderRadius: 8, border: '1px solid var(--border)' }} />
                              <small style={{ display: 'block', color: 'var(--text-dim)', marginTop: 2, fontSize: 'var(--font-xs)' }}>{p.label}</small>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ) : (
                f.key === 'wifi_venue' ? (
                  <AutoTextarea className="form-input" rows={1} value={(show as any)?.wifi_venue ?? ''}
                    placeholder={t(f.phKey!)}
                    disabled={!canWrite}
                    onChange={(e) => updateDayField('wifi_venue', e.target.value)} />
                ) : f.key === 'timezone' ? (
                  /* LOT U2d — le hint (« same time ») passe À CÔTÉ du contrôle,
                     sur la même ligne : sous le champ il créait une 2ᵉ ligne qui
                     déséquilibrait la rangée (et une rangée pleine largeur avec
                     le texte centré dessous serait restée un « trou »). Aucun
                     champ n'est déplacé : même contrôle, même position, le hint
                     suit. Sa largeur reste bornée pour que le champ garde la
                     taille d'un champ court. */
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <AutoTextarea className="form-input" rows={1} value={(show as any)?.timezone ?? ''}
                      style={{ flex: '0 1 380px', minWidth: 0 }}
                      placeholder={t('itinerary.timezonePh')}
                      disabled={!canWrite}
                      onChange={(e) => updateDayField('timezone', e.target.value)} />
                    {tzHint && (
                      <small style={{ color: 'var(--text-dim)', fontSize: 'var(--font-xs)', whiteSpace: 'nowrap' }}>
                        {tzHint}
                      </small>
                    )}
                  </div>
                ) : (
                <AutoTextarea className="form-input" rows={f.full ? 2 : Math.max(1, Math.min(4, String((show as any)?.[f.key] ?? '').split('\n').length))} value={(show as any)?.[f.key] ?? ''}
                  placeholder={t(f.phKey!)}
                  disabled={!canWrite}
                  onChange={(e) => updateDayField(f.key, e.target.value)} />
                )
              )
            }
            {/* Règle U2 : un indice va SOUS le contrôle (colonne 2), jamais dans
                la gouttière de libellé de 118px où il finissait écrasé. */}
            {f.key === 'duration_minutes' && (
              <small style={{ color: 'var(--text-dim)', fontSize: 'var(--font-xs)' }}>
                {t('itinerary.durationOverrideHint')}
              </small>
            )}
            {f.key === 'stage_name' && ((show as any)?.event_type || '').toLowerCase() === 'festival' && (
              <small style={{ color: 'var(--text-dim)', fontSize: 'var(--font-xs)' }}>
                {t('itinerary.stageNameFestival')}
              </small>
            )}
          </div>
        ))}
      </div>
      {saving && <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-xs)' }}>{t('itinerary.saving')}</span>}

      {/* Running order / schedule — FOLDABLE section (user 2026-10-24).
          Auto-open dès que la journée porte des étapes (LOT 1, 2026-11-04). */}
      <details className="rc-section-fold" data-fold="schedule" style={{ marginTop: 20 }} ref={scheduleFold.ref} onToggle={scheduleFold.onToggle}>
        <summary className="rc-section-title" style={{ cursor: 'pointer', listStyle: 'none', margin: '0 0 10px' }}>{withCardIcon('show-day:schedule', t('itinerary.schedule'))}</summary>
      {roadmap.length === 0 ? (
        <p style={{ color: 'var(--text-dim)' }}>{t('itinerary.noSchedule')}</p>
      ) : (
        <div className="rc-list roadmap-list rc-data-block" data-roadmap-list>
          {roadmap.map((item, i) => {
            const category = item.category || '';
            // A legacy value outside the closed list is never rewritten: it
            // shows through the "Other…" free-text option.
            const isOther = otherRows.has(item.id) || (!!category && !SCHEDULE_CATEGORIES.includes(category));
            const expanded = expandedRows.has(item.id)
              || !!item.responsible || !!item.contact_phone || !!item.location;
            const toggleRow = () => setExpandedRows((p) => {
              const next = new Set(p);
              if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
              return next;
            });
            const onCategoryChange = (value: string) => {
              if (value === OTHER_CATEGORY) {
                setOtherRows((p) => new Set(p).add(item.id));
                // Keep the current text; clear only when switching from a list value.
                if (SCHEDULE_CATEGORIES.includes(category)) void updateItem(item, { category: '' });
              } else {
                setOtherRows((p) => { const n = new Set(p); n.delete(item.id); return n; });
                void updateItem(item, { category: value });
              }
            };
            return (
            <div className="rc-card col" data-roadmap-item key={item.id}>
              {/* Line 1: reorder + From/To + category + activity + fold + delete */}
              <div className="rc-card-fields">
                <button className="btn btn-secondary" style={{ padding: '0 6px', fontSize: 'var(--font-xs)', flex: '0 0 auto' }} onClick={() => moveItem(item, -1)} disabled={i === 0 || !canWrite}>↑</button>
                <button className="btn btn-secondary" style={{ padding: '0 6px', fontSize: 'var(--font-xs)', flex: '0 0 auto' }} onClick={() => moveItem(item, 1)} disabled={i === roadmap.length - 1 || !canWrite}>↓</button>
                <input className="form-input" type="time" style={{ flex: '0 0 auto', width: 96 }} value={item.time_from || ''} disabled={!canWrite} onChange={(e) => updateItem(item, { time_from: e.target.value })} />
                <input className="form-input" type="time" style={{ flex: '0 0 auto', width: 96 }} value={item.time_to || ''} disabled={!canWrite} onChange={(e) => updateItem(item, { time_to: e.target.value })} />
                <div className="rc-cell-cat">
                  <select
                    className="form-input"
                    data-category-select
                    aria-label={t('itinerary.categoryPh')}
                    style={{ flex: '1 1 auto', minWidth: 0 }}
                    value={isOther ? OTHER_CATEGORY : category}
                    disabled={!canWrite}
                    onChange={(e) => onCategoryChange(e.target.value)}
                  >
                    {SCHEDULE_CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c || t('itinerary.categoryNone')}</option>
                    ))}
                    <option value={OTHER_CATEGORY}>{t('itinerary.otherCategory')}</option>
                  </select>
                  {isOther && (
                    <input
                      className="form-input"
                      data-category-other
                      style={{ flex: '1 1 90px', minWidth: 0 }}
                      value={category}
                      disabled={!canWrite}
                      placeholder={t('itinerary.categoryOtherPh')}
                      onChange={(e) => updateItem(item, { category: e.target.value })}
                    />
                  )}
                </div>
                <input className="form-input" style={{ flex: '1 1 200px', minWidth: 160 }} value={item.activity} disabled={!canWrite} onChange={(e) => updateItem(item, { activity: e.target.value })} placeholder={t('itinerary.newStepPh')} />
                <button
                  className="btn btn-secondary"
                  data-category-toggle
                  style={{ padding: '0 6px', flex: '0 0 auto' }}
                  aria-label={t('itinerary.moreFields')}
                  aria-expanded={expanded}
                  onClick={toggleRow}
                  disabled={!canWrite}
                >{expanded ? '−' : '＋'}</button>
                <button className="btn btn-secondary" style={{ padding: '0 6px', flex: '0 0 auto' }} onClick={() => removeItem(item)} disabled={!canWrite}>×</button>
              </div>
              {/* Line 2 (collapsed by default): responsible, phone, location */}
              {expanded && (
                <div className="rc-card-fields" data-row-secondary>
                  <input className="form-input" style={{ flex: '1 1 140px', minWidth: 120 }} value={item.responsible || ''} disabled={!canWrite} onChange={(e) => updateItem(item, { responsible: e.target.value })} placeholder={t('itinerary.responsiblePh')} />
                  <input className="form-input" style={{ flex: '1 1 140px', minWidth: 120 }} value={item.contact_phone || ''} disabled={!canWrite} onChange={(e) => updateItem(item, { contact_phone: e.target.value })} placeholder={t('itinerary.phonePh')} />
                  <input className="form-input" style={{ flex: '1 1 140px', minWidth: 120 }} value={item.location || ''} disabled={!canWrite} onChange={(e) => updateItem(item, { location: e.target.value })} placeholder={t('itinerary.locationPh')} />
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
      <button className="btn btn-primary" onClick={addItem} style={{ marginTop: 8 }} disabled={!canWrite}>{t('itinerary.addScheduleStep')}</button>
      </details>

      {/* Distances (airport→hotel, hotel→venue…) — FOLDABLE section.
          Auto-open dès qu'il y a des trajets (LOT 1, 2026-11-04). */}
      <details className="rc-section-fold" data-fold="distances" style={{ marginTop: 16 }} ref={distancesFold.ref} onToggle={distancesFold.onToggle}>
        <summary className="rc-section-title" style={{ cursor: 'pointer', listStyle: 'none', margin: '0 0 10px' }}>{withCardIcon('show-day:distances', t('itinerary.distances'))}</summary>
      <div className="rc-card-fields" style={{ marginBottom: 8 }}>
        <button className="btn btn-secondary" onClick={() => void calculateDistances()} disabled={calcBusy || !canWrite}>
          {calcBusy ? t('itinerary.calculating') : t('itinerary.calculateDistances')}
        </button>
        <span className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-xs)' }}>
          {t('itinerary.distancesHint')}
        </span>
      </div>
      {calcMsg && <p style={{ color: calcMsg.startsWith('Failed') ? 'var(--danger)' : 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 8px' }}>{calcMsg}</p>}
      {distances.length === 0 ? (
        <p style={{ color: 'var(--text-dim)' }}>{t('itinerary.noDistances')}</p>
      ) : (
        <div className="rc-list distance-list rc-data-block" data-distance-list>
          {distances.map((d) => (
            <div className="rc-card" data-distance-item key={d.id} style={{ gap: 6, flexWrap: 'wrap' }}>
              <input className="form-input" style={{ flex: '1 1 120px', minWidth: 100 }} value={d.from_label} disabled={!canWrite} onChange={(e) => updateDistance(d, { from_label: e.target.value })} placeholder={t('itinerary.fromPh')} />
              <span className="text-dim">→</span>
              <input className="form-input" style={{ flex: '1 1 120px', minWidth: 100 }} value={d.to_label} disabled={!canWrite} onChange={(e) => updateDistance(d, { to_label: e.target.value })} placeholder={t('itinerary.toPh')} />
              <select className="form-input" style={{ width: 90 }} value={d.mode || 'car'} disabled={!canWrite} onChange={(e) => updateDistance(d, { mode: e.target.value })}>
                {['car', 'bus', 'van', 'train', 'flight', 'ferry', 'walking'].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <input className="form-input" style={{ width: 80 }} value={d.distance || ''} disabled={!canWrite} onChange={(e) => updateDistance(d, { distance: e.target.value })} placeholder={t('itinerary.distPh')} />
              <input className="form-input" style={{ width: 100 }} value={d.duration || ''} disabled={!canWrite} onChange={(e) => updateDistance(d, { duration: e.target.value })} placeholder={t('itinerary.durationPh')} />
              <input className="form-input" style={{ flex: '1 1 140px', minWidth: 100 }} value={d.notes || ''} disabled={!canWrite} onChange={(e) => updateDistance(d, { notes: e.target.value })} placeholder={t('itinerary.notesPh')} />
              <button className="btn btn-secondary" onClick={() => removeDistance(d)} disabled={!canWrite}>×</button>
            </div>
          ))}
        </div>
      )}
      <button className="btn btn-primary" onClick={addDistance} style={{ marginTop: 8 }} disabled={!canWrite}>{t('itinerary.addDistance')}</button>
      </details>

      {/* Contact persons (SAMAEL "CONTACT PERSONS" block) — FOLDABLE (user 2026-10-24).
          Auto-open dès que des contacts existent (LOT 1, 2026-11-04). */}
      <details className="rc-section-fold" data-fold="contacts" style={{ marginTop: 16 }} ref={contactsFold.ref} onToggle={contactsFold.onToggle}>
        <summary className="rc-section-title" style={{ cursor: 'pointer', listStyle: 'none', margin: '0 0 10px' }}>{withCardIcon('show-day:contacts', t('contactPersons.title'))}</summary>
        <ContactPersonsSection realId={realId} bandId={bandId} onHasDataChange={setContactsHasData} />
      </details>
    </div>
  );
}